import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ApprovalNoticeBox from '@/components/Approval/@components/ApprovalNoticeBox';
import {
  buildInfluencerPplContentHtml,
  InfluencerPplDetailDialog,
  InfluencerPplSnapshot,
} from '@/components/Approval/@components/InfluencerPpl';
import RHFTextArea from '@/components/HookForm/RHFTextarea';
import { getInfluencerPplDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import {
  buildInfluencerPplDefaultPurpose,
  createInfluencerPplState,
  INFLUENCER_PPL_LOAD_FAILED_MESSAGE,
  INFLUENCER_PPL_SEQUENCE_MISSING_MESSAGE,
  toInfluencerPplSavedInfo,
  type InfluencerPplDraftSource,
} from '@/utils/approval/influencerPpl';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

type InfluencerPplFieldProps = {
  /** 신규는 양식 응답, 편집은 저장값 복원이 끝난 뒤 true — 그때 문서 일련번호로 1회 조회 */
  ready: boolean;
};

/**
 * 인플루언서 PPL 정산(135) — 문서 일련번호로 PPL 정산 리포트를 조회해 표·그래프로.
 * 제목은 조회 결과로 덮어씀(레거시 동일). 편집 재진입은 목적만 저장 문구로 채움
 */
const InfluencerPplField = ({ ready }: InfluencerPplFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, getValues, setValue } = useFormContext<ApprovalFormValues>();
  const report = useWatch({ control, name: 'influencerPpl.report' });
  const loading = useWatch({ control, name: 'influencerPpl.loading' });
  const failed = useWatch({ control, name: 'influencerPpl.failed' });
  const [detailOpen, setDetailOpen] = useState(false);
  const startedRef = useRef(false);
  const aliveRef = useRef(true);

  useEffect(
    () => () => {
      aliveRef.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!ready || startedRef.current) return;
    startedRef.current = true;
    const initial = getValues('influencerPpl');
    const documentSeq = initial?.documentSeq ?? null;
    if (!documentSeq) {
      snackbar.showError({
        message: t(INFLUENCER_PPL_SEQUENCE_MISSING_MESSAGE),
      });
      return;
    }
    const savedPurpose = initial?.savedPurpose;
    setValue('influencerPpl', {
      ...createInfluencerPplState(documentSeq, savedPurpose),
      loading: true,
    });
    getNestedDataFetcher<InfluencerPplDraftSource>(
      getInfluencerPplDraftSourceUrl(documentSeq),
    )
      .then((res) => {
        if (!aliveRef.current) return;
        const source = res.display?.source;
        if (!source) throw new Error('empty source');
        const brand = source.brdNm ?? '';
        const autoSubject = t('{{brand}} 마케팅 진행의 건', {
          brand,
          interpolation: { escapeValue: false },
        });
        const defaultPurpose = buildInfluencerPplDefaultPurpose(brand, t);
        setValue('influencerPpl', {
          ...createInfluencerPplState(documentSeq, savedPurpose),
          report: source,
          purpose: savedPurpose ?? defaultPurpose,
          defaultPurpose,
          autoSubject,
        });
        setValue('subject', autoSubject, { shouldDirty: false });
      })
      .catch(() => {
        if (!aliveRef.current) return;
        setValue('influencerPpl', {
          ...createInfluencerPplState(documentSeq, savedPurpose),
          failed: true,
        });
        snackbar.showError({ message: t(INFLUENCER_PPL_LOAD_FAILED_MESSAGE) });
      });
  }, [ready, getValues, setValue, snackbar, t]);

  // 목적은 입력할 때마다 표를 다시 만들지 않도록 빈 칸으로 그리고 입력칸을 끼움
  const content = useMemo(
    () =>
      report ? buildInfluencerPplContentHtml({ report, purpose: '', t }) : '',
    [report, t],
  );
  const info = useMemo(
    () => (report ? toInfluencerPplSavedInfo(report) : null),
    [report],
  );
  const purposeInput = useMemo(
    () => <RHFTextArea name="influencerPpl.purpose" minRows={5} />,
    [t],
  );

  return (
    <FieldBlock className="influencer-ppl">
      {loading && (
        <ApprovalNoticeBox role="status">
          {t('데이터를 불러오는 중입니다...')}
        </ApprovalNoticeBox>
      )}
      {failed && (
        <ApprovalNoticeBox role="alert">
          {t(INFLUENCER_PPL_LOAD_FAILED_MESSAGE)}
        </ApprovalNoticeBox>
      )}
      {report && (
        <>
          <InfluencerPplSnapshot
            content={content}
            info={info}
            showExposure={false}
            onOpenDetail={() => setDetailOpen(true)}
            purposeInput={purposeInput}
          />
          <InfluencerPplDetailDialog
            open={detailOpen}
            onClose={() => setDetailOpen(false)}
            brand={report.brdNm ?? ''}
            detailInfo={report.detailInfo ?? null}
          />
        </>
      )}
    </FieldBlock>
  );
};

export default InfluencerPplField;
